import { useState } from "react";
import { MessageSquare, Trash2, Plus, Pencil, Check, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export interface Conversation {
  id: string;
  user_id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

interface ConversationsListProps {
  conversations: Conversation[];
  currentConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: string) => void;
  onRenameConversation: (id: string, newTitle: string) => void;
}

export const ConversationsList = ({
  conversations,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  onRenameConversation,
}: ConversationsListProps) => {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'de' ? de : enUS;
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const handleStartEdit = (conv: Conversation, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(conv.id);
    setEditValue(conv.title);
  };

  const handleSaveEdit = (id: string) => {
    if (editValue.trim() && editValue !== conversations.find(c => c.id === id)?.title) {
      onRenameConversation(id, editValue.trim());
    }
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <Button 
          onClick={onNewConversation}
          className="w-full gap-2"
          size="default"
        >
          <Plus className="h-4 w-4" />
          Neuer Chat
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {conversations.map((conv) => (
            <div
              key={conv.id}
              className={`group relative flex items-start gap-3 px-3 py-3 rounded-lg cursor-pointer transition-all duration-200 ${
                currentConversationId === conv.id
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'hover:bg-accent/50'
              }`}
              onClick={() => editingId !== conv.id && onSelectConversation(conv.id)}
            >
              <MessageSquare className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                {editingId === conv.id ? (
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit(conv.id);
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      className="flex-1 px-2 py-1.5 text-sm bg-background text-foreground border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                      autoFocus
                      maxLength={50}
                    />
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => handleSaveEdit(conv.id)}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={handleCancelEdit}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium line-clamp-2 leading-tight mb-1">
                          {conv.title}
                        </p>
                        <p className={`text-xs ${
                          currentConversationId === conv.id 
                            ? 'text-primary-foreground/70' 
                            : 'text-muted-foreground'
                        }`}>
                          {formatDistanceToNow(new Date(conv.updated_at), {
                            addSuffix: true,
                            locale,
                          })}
                        </p>
                      </div>
                      
                      {/* Action Buttons - Show on hover or when active */}
                      <div className={`flex gap-1 flex-shrink-0 transition-opacity duration-200 ${
                        currentConversationId === conv.id 
                          ? 'opacity-100' 
                          : 'opacity-0 group-hover:opacity-100'
                      }`}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-7 w-7 p-0 ${
                            currentConversationId === conv.id
                              ? 'hover:bg-primary-foreground/20'
                              : ''
                          }`}
                          onClick={(e) => handleStartEdit(conv, e)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-7 w-7 p-0 ${
                                currentConversationId === conv.id
                                  ? 'hover:bg-primary-foreground/20'
                                  : ''
                              }`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Konversation löschen?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Diese Aktion kann nicht rückgängig gemacht werden. Die Konversation und alle Nachrichten werden dauerhaft gelöscht.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                              <AlertDialogAction onClick={() => onDeleteConversation(conv.id)}>
                                Löschen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}

          {conversations.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-12 px-4">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Keine Konversationen</p>
              <p className="text-xs mt-1">Starten Sie einen neuen Chat</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
